import { useTranslation } from 'react-i18next'
import { PackagingItemDocumentsPanel } from '@/shared/components/packaging/PackagingItemDocumentsPanel'
import type { PackagingItemDocumentsPanelLabels } from '@/shared/components/packaging/packagingDocumentTypes'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import {
  useAttachPackagingItemDocument,
  useDeletePackagingItemDocument,
  useDownloadPackagingHeavyMetalsConformityStatement,
  useDownloadPackagingSubstancesOfConcernConformityStatement,
  useGetPackagingItem,
  useIngestAllPackagingItemDocuments,
  useIngestPackagingItemDocument,
  useRetryPackagingItemDocumentIngestion,
  useListPackagingSupplierDocuments,
  usePackagingDocumentDownloadUrl,
  usePackagingItemDocuments,
  useUploadPackagingItemDocument,
} from '@/shared/hooks/api/packagingSuppliers'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { getPackagingDocumentPanelPermissions } from '@/shared/lib/readOnlyAccess'
import { MAX_UPLOAD_FILE_SIZE_MB } from '@/shared/lib/uploadGuardrails'

type LexItemDocumentsPanelProps = {
  supplierId: string
  itemId: string
}

export function LexItemDocumentsPanel({
  supplierId,
  itemId,
}: LexItemDocumentsPanelProps) {
  const { t, i18n } = useTranslation([
    'packagingSuppliers',
    'common',
    'productCatalog',
  ])
  const canWriteOrgData = useCanWriteOrgData()
  const { data: session } = useGetUserSession()
  const isArtemAdmin = session?.isArtemAdmin === true
  // Batch ingest and diagnostics stay @artem.earth-only (`isArtemAdmin`).
  const canAccessDocumentIngestion = isArtemAdmin
  const canRetryDocumentIngestion = canWriteOrgData
  const documentPermissions =
    getPackagingDocumentPanelPermissions(canWriteOrgData)
  const { data: item } = useGetPackagingItem(supplierId, itemId)
  const { data: documentsFromQuery, isLoading } = usePackagingItemDocuments(
    supplierId,
    itemId,
    { pollIngestion: true }
  )
  const documents = documentsFromQuery ?? item?.documents ?? []
  const ingestAllDocuments = useIngestAllPackagingItemDocuments(
    supplierId,
    itemId
  )
  const ingestDocument = useIngestPackagingItemDocument(supplierId, itemId)
  const retryDocument = useRetryPackagingItemDocumentIngestion(
    supplierId,
    itemId
  )
  const uploadDocument = useUploadPackagingItemDocument(supplierId, itemId)
  const attachDocument = useAttachPackagingItemDocument(supplierId, itemId)
  const { data: libraryDocuments = [] } =
    useListPackagingSupplierDocuments(supplierId)
  const deleteDocument = useDeletePackagingItemDocument(supplierId, itemId)
  const downloadDocument = usePackagingDocumentDownloadUrl(supplierId, itemId)
  const downloadHeavyMetalsStatement =
    useDownloadPackagingHeavyMetalsConformityStatement(supplierId, itemId)
  const downloadSubstancesOfConcernStatement =
    useDownloadPackagingSubstancesOfConcernConformityStatement(
      supplierId,
      itemId
    )

  const conformityStatementLocale = i18n.language.startsWith('zh')
    ? 'zh-CN'
    : 'en-GB'

  const labels: PackagingItemDocumentsPanelLabels = {
    title: t('itemDetail.documents'),
    uploadDocument: t('itemDetail.uploadDocument'),
    uploading: t('itemDetail.uploading'),
    uploadFailed: t('itemDetail.uploadFailed'),
    uploadRightsProtected: t('itemDetail.uploadRightsProtected'),
    uploadEncrypted: t('itemDetail.uploadEncrypted'),
    uploadInvalidPdf: t('itemDetail.uploadInvalidPdf'),
    uploadInvalidLegacyXls: t('itemDetail.uploadInvalidLegacyXls'),
    uploadFileTooLarge: t('itemDetail.uploadFileTooLarge'),
    loadingDocuments: t('itemDetail.loadingDocuments'),
    noDocuments: t('itemDetail.noDocuments'),
    download: t('itemDetail.download'),
    delete: t('common:actions.delete', { defaultValue: 'Delete' }),
    documentType: t('itemDetail.documentType'),
    selectDocumentType: t('itemDetail.selectDocumentType'),
    uploadDialogTitle: t('itemDetail.uploadDialogTitle'),
    uploadDialogDescription: t('itemDetail.uploadDialogDescription'),
    uploadDialogRequirementFormats: t(
      'itemDetail.uploadDialogRequirementFormats'
    ),
    uploadDialogRequirementMaxSize: t(
      'itemDetail.uploadDialogRequirementMaxSize',
      { maxSizeMb: MAX_UPLOAD_FILE_SIZE_MB }
    ),
    uploadDialogAssessingFile: t('itemDetail.uploadDialogAssessingFile'),
    uploadDialogDragDrop: t('itemDetail.uploadDialogDragDrop'),
    uploadDialogOrClick: t('itemDetail.uploadDialogOrClick'),
    uploadDialogSelectFiles: t('itemDetail.uploadDialogSelectFiles'),
    uploadUnsupportedFileType: t('itemDetail.uploadUnsupportedFileType'),
    uploadEmptyFile: t('itemDetail.uploadEmptyFile'),
    uploadDialogDone: t('itemDetail.uploadDialogDone'),
    uploadDialogMultipleDropped: t('itemDetail.uploadDialogMultipleDropped'),
    uploadDialogConfirmUpload: t('itemDetail.uploadDialogConfirmUpload'),
    uploadDialogChooseDifferentFile: t(
      'itemDetail.uploadDialogChooseDifferentFile'
    ),
    librarySectionTitle: t('itemDetail.librarySectionTitle'),
    librarySearchPlaceholder: t('itemDetail.librarySearchPlaceholder'),
    libraryEmpty: t('itemDetail.libraryEmpty'),
    libraryEmptyAllLinked: t('itemDetail.libraryEmptyAllLinked'),
    librarySearchEmpty: t('itemDetail.librarySearchEmpty'),
    libraryLinkedItemCount: t('itemDetail.libraryLinkedItemCount'),
    libraryAttachAction: t('itemDetail.libraryAttachAction'),
    libraryAttaching: t('itemDetail.libraryAttaching'),
    sameNameWarningTitle: t('itemDetail.sameNameWarningTitle'),
    sameNameWarningDescription: t('itemDetail.sameNameWarningDescription'),
    sameNameAlreadyLinkedDescription: t(
      'itemDetail.sameNameAlreadyLinkedDescription'
    ),
    sameNameReuseCta: t('itemDetail.sameNameReuseCta'),
    attachSuccess: t('itemDetail.attachSuccess'),
    uploadSuccess: t('itemDetail.uploadSuccess'),
    attachFailed: t('itemDetail.attachFailed'),
    uploadDialogTypeAlreadyUploaded: t(
      'itemDetail.uploadDialogTypeAlreadyUploaded'
    ),
    columnType: t('itemDetail.columnType'),
    columnFile: t('itemDetail.columnFile'),
    columnSize: t('itemDetail.columnSize'),
    ingestDocuments: t('itemDetail.ingestDocuments'),
    ingestingDocuments: t('itemDetail.ingestingDocuments'),
    ingestDocument: t('itemDetail.ingestDocument'),
    retryDocument: t('itemDetail.retryDocument'),
    reIngestDocument: t('itemDetail.reIngestDocument'),
    columnIngestion: t('itemDetail.columnIngestion'),
    linkedDuplicateBadge: t('itemDetail.linkedDuplicateBadge'),
    linkedDuplicateTooltip: t('itemDetail.linkedDuplicateTooltip'),
    linkedDuplicateViewCanonical: t('itemDetail.linkedDuplicateViewCanonical'),
    ingestionNotStarted: t('itemDetail.ingestionNotStarted'),
    ingestionStatusLabels: {
      PENDING: t('itemDetail.ingestionStatuses.PENDING'),
      PROCESSING: t('itemDetail.ingestionStatuses.PROCESSING'),
      COMPLETED: t('itemDetail.ingestionStatuses.COMPLETED'),
      FAILED: t('itemDetail.ingestionStatuses.FAILED'),
    },
    mandatoryDocuments: t('itemDetail.mandatoryDocuments'),
    missingMandatoryDocuments: t('itemDetail.missingMandatoryDocuments'),
    mandatoryComplete: t('itemDetail.mandatoryComplete'),
    mandatoryMissing: t('itemDetail.mandatoryMissing'),
    downloadHeavyMetalsStatement: t('itemDetail.downloadHeavyMetalsStatement'),
    downloadSubstancesOfConcernStatement: t(
      'itemDetail.downloadSubstancesOfConcernStatement'
    ),
    typeLabels: {
      DIELINE_DIAGRAM: t('itemDetail.documentTypes.dielineDiagram'),
      PACKAGING_PHOTOS: t('itemDetail.documentTypes.packagingPhotos'),
      LABEL_PHOTOS: t('itemDetail.documentTypes.labelPhotos'),
      SDS_OR_BOM: t('itemDetail.documentTypes.sdsOrBom'),
      HEAVY_METALS_TEST_REPORT: t(
        'itemDetail.documentTypes.heavyMetalsTestReport'
      ),
      HEAVY_METALS_CONFORMITY_STATEMENT: t(
        'itemDetail.documentTypes.heavyMetalsConformityStatement'
      ),
      SUBSTANCES_OF_CONCERN_TEST_REPORT: t(
        'itemDetail.documentTypes.substancesOfConcernTestReport'
      ),
      SUBSTANCES_OF_CONCERN_CONFORMITY_STATEMENT: t(
        'itemDetail.documentTypes.substancesOfConcernConformityStatement'
      ),
      OTHER: t('itemDetail.documentTypes.other'),
      OTHER_TEST_REPORT: t('itemDetail.documentTypes.otherTestReport'),
    },
    shortTypeLabels: {
      DIELINE_DIAGRAM: t('itemDetail.shortDocumentTypes.dielineDiagram'),
      PACKAGING_PHOTOS: t('itemDetail.shortDocumentTypes.packagingPhotos'),
      LABEL_PHOTOS: t('itemDetail.shortDocumentTypes.labelPhotos'),
      SDS_OR_BOM: t('itemDetail.shortDocumentTypes.sdsOrBom'),
      HEAVY_METALS_TEST_REPORT: t(
        'itemDetail.shortDocumentTypes.heavyMetalsTestReport'
      ),
      HEAVY_METALS_CONFORMITY_STATEMENT: t(
        'itemDetail.shortDocumentTypes.heavyMetalsConformityStatement'
      ),
      SUBSTANCES_OF_CONCERN_TEST_REPORT: t(
        'itemDetail.shortDocumentTypes.substancesOfConcernTestReport'
      ),
      SUBSTANCES_OF_CONCERN_CONFORMITY_STATEMENT: t(
        'itemDetail.shortDocumentTypes.substancesOfConcernConformityStatement'
      ),
      OTHER: t('itemDetail.shortDocumentTypes.other'),
      OTHER_TEST_REPORT: t('itemDetail.shortDocumentTypes.otherTestReport'),
    },
  }

  return (
    <PackagingItemDocumentsPanel
      documents={documents}
      documentRequirements={item?.documentRequirements}
      heavyMetalsStatus={item?.heavyMetalsStatus}
      substancesOfConcernStatus={item?.substancesOfConcernStatus}
      isLoadingDocuments={isLoading}
      isUploading={uploadDocument.isPending}
      isAttaching={attachDocument.isPending}
      isDeleting={deleteDocument.isPending}
      isDownloading={downloadDocument.isPending}
      isDownloadingHeavyMetalsStatement={downloadHeavyMetalsStatement.isPending}
      isDownloadingSubstancesOfConcernStatement={
        downloadSubstancesOfConcernStatement.isPending
      }
      labels={labels}
      libraryDocuments={libraryDocuments}
      onUploadDialogOpenChange={(open) => {
        if (!open) {
          uploadDocument.reset()
          attachDocument.reset()
        }
      }}
      onUpload={async (file, documentType) => {
        await uploadDocument.mutateAsync({ file, documentType })
      }}
      onAttach={async (documentId, documentType) => {
        await attachDocument.mutateAsync({
          packagingSupplierDocumentId: documentId,
          documentType,
        })
      }}
      onDelete={async (documentId) => {
        await deleteDocument.mutateAsync(documentId)
      }}
      onDownload={async (documentId) => {
        await downloadDocument.mutateAsync(documentId)
      }}
      onDownloadHeavyMetalsStatement={async () => {
        await downloadHeavyMetalsStatement.mutateAsync(
          conformityStatementLocale
        )
      }}
      onDownloadSubstancesOfConcernStatement={async () => {
        await downloadSubstancesOfConcernStatement.mutateAsync(
          conformityStatementLocale
        )
      }}
      canUpload={documentPermissions.canUpload}
      canDelete={documentPermissions.canDelete}
      showIngestionAdmin={canAccessDocumentIngestion}
      isIngestingAll={ingestAllDocuments.isPending}
      onIngestAll={
        canAccessDocumentIngestion
          ? async () => {
              await ingestAllDocuments.mutateAsync()
            }
          : undefined
      }
      ingestingDocumentId={
        ingestDocument.isPending ? (ingestDocument.variables ?? null) : null
      }
      onIngestDocument={
        canAccessDocumentIngestion || canRetryDocumentIngestion
          ? async (documentId) => {
              await ingestDocument.mutateAsync(documentId)
            }
          : undefined
      }
      onRetryDocument={
        canWriteOrgData && !canAccessDocumentIngestion
          ? async (documentId) => {
              await retryDocument.mutateAsync(documentId)
            }
          : undefined
      }
      retryingDocumentId={
        retryDocument.isPending ? (retryDocument.variables ?? null) : null
      }
    />
  )
}
